import { getAuthCookie } from "./authCookies";

/** CRUD actions returned by /permission-scope/module-permissions */
export type ModuleAction = "create" | "view" | "edit" | "delete";

/**
 * Canonical module_name values for listing/datatable pages.
 * Pass these to useModuleActionPermissions(moduleName).
 */
export const MODULE_PERMISSION_NAMES = {
  // Signage
  agents: "agents",
  agent_inventory: "agent_inventory",
  brands: "brands",
  brokerage_firms: "brokerage_firms",
  categories: "categories",
  customer_groups: "customer_groups",
  damaged_products: "damaged_products",
  dynamic_costs: "dynamic_costs",
  agent_incoming_products: "agent_incoming_products",
  office_incoming_products: "office_incoming_products",
  active_installations: "active_installations",
  installation_orders: "installation_orders",
  linked_installer: "linked_installer",
  suppliers: "suppliers",
  lost_products: "lost_products",
  office_inventory: "office_inventory",
  orders: "orders",
  org_hierarchies: "org_hierarchies",
  permission_scopes: "permission_scopes",
  permissions: "permissions",
  attributes: "attributes",
  removal_orders: "removal_orders",
  assets: "assets",
  saved_cards: "saved_cards",
  scheduled_jobs: "scheduled_jobs",
  standard_inventory: "standard_inventory",
  signage_team_members: "signage_team_members",
  users: "users",
  warehouses: "warehouses",
  studio: "studio",
  projects: "projects",
  batch_payments: "batch_payments",
  payment_methods: "payment_methods",
  special_fees: "special_fees",
  discounts: "discounts",
  shipping_presets: "shipping_presets",
  buy_labels: "buy_labels",
  postage_wallet: "postage_wallet",
  shipping_methods_cost: "shipping_methods_cost",
  integrations: "integrations",
  invoice_templates: "invoice_templates",
  overdue_trigger_configuration: "overdue_trigger_configuration",
  general_settings: "general_settings",
  post_types: "post_types",
  marketplace_profile: "marketplace_profile",
  reports: "reports",
  dash_board: "dash_board",

  // Brokerage
  brokerage_agents: "brokerage_agents",
} as const;

export type ModulePermissionName =
  | (typeof MODULE_PERMISSION_NAMES)[keyof typeof MODULE_PERMISSION_NAMES]
  | string;

export type ModuleActionFlags = {
  canCreate: boolean;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
};

export type ModulePermissionsResponse = {
  module?: string;
  permissions?: string[];
  user_id?: string;
};

const ALL_ACTIONS: ModuleAction[] = ["create", "view", "edit", "delete"];

const TEAM_MEMBER_LICENSE_TYPES = new Set([
  "SIGNAGE_TEAM_MEMBER",
  "BROKERAGE_TEAM_MEMBER",
  "TEAM_MEMBER",
  "TEAMMEMBER",
]);

/** True when logged-in user is a signage or brokerage team member. */
export function isTeamMemberUser(): boolean {
  const licenseType = String(getAuthCookie("licenseType") || "")
    .trim()
    .toUpperCase();
  return TEAM_MEMBER_LICENSE_TYPES.has(licenseType);
}

export function actionsToFlags(actions: Iterable<string>): ModuleActionFlags {
  const set = new Set(
    Array.from(actions, (a) => String(a || "").trim().toLowerCase()).filter(Boolean)
  );
  return {
    canCreate: set.has("create"),
    canView: set.has("view"),
    canEdit: set.has("edit"),
    canDelete: set.has("delete"),
  };
}

export function allActionsAllowed(): ModuleActionFlags {
  return actionsToFlags(ALL_ACTIONS);
}

export function noActionsAllowed(): ModuleActionFlags {
  return {
    canCreate: false,
    canView: false,
    canEdit: false,
    canDelete: false,
  };
}

/**
 * GET /permission-scope/module-permissions?module_name=&user_id=
 * Returns normalized action list (lowercase).
 */
export async function fetchModuleActionPermissions(
  moduleName: string,
  options?: { userId?: string; token?: string; signal?: AbortSignal }
): Promise<ModuleAction[]> {
  const module = String(moduleName || "").trim();
  const userId = String(options?.userId || getAuthCookie("userId") || "").trim();
  const token = String(options?.token || getAuthCookie("sessionToken") || "").trim();

  if (!module || !userId || !token) {
    return [];
  }

  const url =
    `${process.env.REACT_APP_API_URL}/permission-scope/module-permissions` +
    `?module_name=${encodeURIComponent(module)}` +
    `&user_id=${encodeURIComponent(userId)}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal: options?.signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to load module permissions (${response.status})`);
  }

  const data = (await response.json()) as ModulePermissionsResponse;
  const list = Array.isArray(data?.permissions) ? data.permissions : [];

  return list
    .map((p) => String(p || "").trim().toLowerCase())
    .filter((p): p is ModuleAction =>
      p === "create" || p === "view" || p === "edit" || p === "delete"
    );
}
